import { OptionsApi } from '@core/swagger-client';
import { QuestionType, SurveyWithPoints } from '@core/types';
import {
    Button,
    CompositePageHeader,
    ContainerNoTopSpace,
    ContainerWebinarInner,
    ContentHeader,
    Delete,
    Edit,
    ErrorMessagePage,
    Heading,
    Input,
    LoaderPage,
    PageHeader,
    PageHeaderContent,
    Plus,
    SortArrow,
    TabGroup,
    TextArea,
    WarningOutlined
} from '@core/ui';
import { useLocation, useNavigate, useParams } from '@online-webinar/router';
import { BackToOriginLink } from '@online-webinar/ui-frontend';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isEqual } from 'lodash-es';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EditQuestion } from '../../organisms/EditQuestion';
import { useDefaultSurvey } from './default-survey';

interface SurveyWithPointsEx extends SurveyWithPoints {
    version: number;
}

export const EditSurveyPage = () => {
    const tabs = ['intro', 'questions', 'outro'];
    const { hash } = useLocation();
    let activeTab = tabs.findIndex((t) => t === hash);
    if (activeTab === -1) {
        activeTab = 0;
    }

    const defaultSurvey = useDefaultSurvey();
    const { webinarId } = useParams({ strict: false });
    const { t } = useTranslation();

    const [survey, setSurvey] = useState<SurveyWithPointsEx>(null!);
    const [savedSurvey, setSavedSurvey] = useState<SurveyWithPointsEx | null>(null);
    const [saveState, setSaveState] = useState<'saved' | 'saving' | 'error'>('saved');
    const [activeQuestion, setActiveQuestion] = useState<string | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);
    const navigate = useNavigate();

    const { data: webinar, isLoading: webinarIsLoading, error: webinarError } = useQuery(OptionsApi.webinar.get(webinarId!));
    const { data: surveyData, isLoading: surveyIsLoading, error: surveyError } = useQuery(OptionsApi.survey.get(webinarId!, { refetchOnWindowFocus: false }));

    const { mutate: saveSurvey } = useMutation(OptionsApi.survey.update());

    useEffect(() => {
        if (surveyData) {
            const version = new Date().getTime();
            if (surveyData.useDefault) {
                setSavedSurvey({ ...defaultSurvey, version });
                setSurvey({ ...defaultSurvey, version });
            } else {
                setSavedSurvey({ ...surveyData, version });
                setSurvey({ ...surveyData, version });
            }
        }
    }, [surveyData]);

    const changeTab = (no: number) => {
        navigate({ hash: tabs[no] });
    };

    const validateSurvey = (validate: SurveyWithPoints): boolean => {
        if (!validate.enabled) {
            setValidationError(null);
            return false;
        }

        if (!validate.title) {
            setValidationError(t('EditSurvey.Validation.TitleRequired'));
            return false;
        }
        if (!validate.intro) {
            setValidationError(t('EditSurvey.Validation.IntroRequired'));
            return false;
        }
        if (!validate.outro) {
            setValidationError(t('EditSurvey.Validation.OutroRequired'));
            return false;
        }
        if (validate.questions.length === 0) {
            setValidationError(t('EditSurvey.Validation.QuestionsRequired'));
            return false;
        }
        let index = 0;
        if ((index = validate.questions.findIndex((q) => !q.questionText)) !== -1) {
            setValidationError(t('EditQuestionnaire.Validation.QuestionRequired', { question: index + 1 }));
            return false;
        }
        if ((index = validate.questions.findIndex((q) => q.questionType !== QuestionType.Open && q.hasOtherOption && !q.otherOptionText)) !== -1) {
            setValidationError(t('EditQuestionnaire.Validation.OtherOptionTextRequired', { question: index + 1 }));
            return false;
        }
        if ((index = validate.questions.findIndex((q) => q.questionType !== QuestionType.Open && q.options.length === 0)) !== -1) {
            setValidationError(t('EditQuestionnaire.Validation.OptionsRequired', { question: index + 1 }));
            return false;
        }
        if (
            (index = validate.questions.findIndex((q) => q.questionType !== QuestionType.Open && q.options.some((o) => !o.text || o.text.trim() === ''))) !== -1
        ) {
            const question = validate.questions[index];
            const optionIndex = question.options.findIndex((o) => !o.text || o.text.trim() === '');
            setValidationError(t('EditQuestionnaire.Validation.OptionTextRequired', { question: index + 1, option: optionIndex + 1 }));
            return false;
        }
        if ((index = validate.questions.findIndex((q) => q.questionType !== QuestionType.Open && q.options.some((o) => !Number.isInteger(o.value)))) !== -1) {
            const question = validate.questions[index];
            const optionIndex = question.options.findIndex((o) => !Number.isInteger(o.value));
            setValidationError(t('EditQuestionnaire.Validation.OptionValueRequired', { question: index + 1, option: optionIndex + 1 }));
            return false;
        }
        setValidationError(null);
        return true;
    };

    useEffect(() => {
        if (!survey) {
            return;
        }

        if (isEqual(savedSurvey, survey)) {
            return;
        }

        if (webinarIsLoading || surveyIsLoading) {
            return;
        }

        const saving = { ...survey };
        const save = { ...survey, isValid: validateSurvey(survey) };
        setSaveState('saving');
        const handle = setTimeout(() => {
            saveSurvey(
                { webinarId: webinarId!, survey: save },
                {
                    onSuccess: () => {
                        setSaveState('saved');
                        setSavedSurvey(saving);
                    },
                    onError: () => setSaveState('error')
                }
            );
        }, 1000);
        return () => clearTimeout(handle);
    }, [survey, savedSurvey, webinarIsLoading, surveyIsLoading]);

    const error = webinarError || surveyError;
    if (error) {
        return <ErrorMessagePage apiError={error} />;
    }

    if (!webinar || !survey || webinarIsLoading || surveyIsLoading) {
        return <LoaderPage></LoaderPage>;
    }

    const addQuestion = (type: QuestionType) => {
        const questionId = crypto.randomUUID();
        setActiveQuestion(questionId);
        setSurvey({
            ...survey,
            questions: [
                ...survey.questions,
                {
                    questionId,
                    questionText: '',
                    questionType: type,
                    hasOtherOption: false,
                    options: [],
                    required: false
                }
            ],
            version: survey.version + 1
        });
    };

    const moveQuestionUp = (index: number) => {
        if (index > 0) {
            const updatedQuestions = [...survey.questions];
            [updatedQuestions[index - 1], updatedQuestions[index]] = [updatedQuestions[index], updatedQuestions[index - 1]];
            setSurvey({ ...survey, questions: updatedQuestions, version: survey.version + 1 });
        }
    };

    const moveQuestionDown = (index: number) => {
        if (index < survey.questions.length - 1) {
            const updatedQuestions = [...survey.questions];
            [updatedQuestions[index], updatedQuestions[index + 1]] = [updatedQuestions[index + 1], updatedQuestions[index]];
            setSurvey({ ...survey, questions: updatedQuestions, version: survey.version + 1 });
        }
    };

    return (
        <ContainerNoTopSpace>
            <PageHeader>
                <PageHeaderContent>
                    <CompositePageHeader title={webinar.title} broadcastDateTime={webinar.broadcastDateTime} />
                </PageHeaderContent>
            </PageHeader>
            <ContainerWebinarInner>
                <BackToOriginLink fallback="content" />
                <div>
                    {saveState === 'saved' && <p className="text-success mb-4">{t('EditSurvey.Save.Saved')}</p>}
                    {saveState === 'saving' && <p className="text-orange-500 mb-4">{t('EditSurvey.Save.Saving')}</p>}
                    {saveState === 'error' && <p className="text-warning mb-4">{t('EditSurvey.Save.Error')}</p>}
                </div>
                <ContentHeader contentHeader={t('EditSurvey.Pageheading')} />
                {validationError && (
                    <div className="inline-flex items-center bg-warning-light rounded-md text-sm mb-6 pl-3 pr-4 py-2">
                        <WarningOutlined classNames="w-6 md:w-8 fill-warning -mt-0.5 mr-2 flex-none" /> {validationError}
                    </div>
                )}
                <div className="max-w-4xl">
                    <div className="max-w-lg">
                        <Input
                            classNames="mb-6"
                            label={t('EditSurvey.Label.Title')}
                            value={survey.title}
                            onChange={(e) => setSurvey({ ...survey, title: e.target.value, version: survey.version + 1 })}
                        />
                    </div>
                    <div className="pl-10 mb-2">
                        <input
                            type="checkbox"
                            role="switch"
                            id="enabled"
                            checked={survey.enabled}
                            onChange={() => setSurvey({ ...survey, enabled: !survey.enabled, version: survey.version + 1 })}
                            className="w-2em h-1em appearance-none bg-inherit bg-switch bg-[center_left] focus-visible:not-checked:bg-switch-focus checked:bg-primary checked:bg-switch-checked checked:bg-[center_right] bg-no-repeat border-2 border-gray-500 focus-visible:border-primary checked:border-primary rounded-full float-left mt-0.25em -ml-10 outline-hidden focus-visible:shadow-[0_0_0_.25rem_hsl(var(--c-primary)/.3)] motion-safe:transition-[background-position] duration-150 ease-in-out"
                        />
                        <label htmlFor="enabled" className="inline-block">
                            {t('EditSurvey.Label.Enabled')}
                        </label>
                    </div>
                    <div className="pl-10 mb-10">
                        <input
                            type="checkbox"
                            role="switch"
                            id="required"
                            checked={survey.required}
                            onChange={() => setSurvey({ ...survey, required: !survey.required, version: survey.version + 1 })}
                            className="w-2em h-1em appearance-none bg-inherit bg-switch bg-[center_left] focus-visible:not-checked:bg-switch-focus checked:bg-primary checked:bg-switch-checked checked:bg-[center_right] bg-no-repeat border-2 border-gray-500 focus-visible:border-primary checked:border-primary rounded-full float-left mt-0.25em -ml-10 outline-hidden focus-visible:shadow-[0_0_0_.25rem_hsl(var(--c-primary)/.3)] motion-safe:transition-[background-position] duration-150 ease-in-out"
                        />
                        <label htmlFor="required" className="inline-block">
                            {t('EditSurvey.Label.Required')}
                        </label>
                    </div>
                    <TabGroup direction={TabGroup.direction.HORIZONTAL}>
                        <TabGroup.TabList numTabs={3} activeTab={activeTab} setActiveTab={changeTab}>
                            <TabGroup.Tab activeTab={activeTab} setActiveTab={changeTab} index={0} className="nav-tab" activeClassName="nav-tab--active">
                                <span onClick={() => changeTab(0)}>{t('EditSurvey.Tab.Intro')}</span>
                            </TabGroup.Tab>
                            <TabGroup.Tab activeTab={activeTab} setActiveTab={changeTab} index={1} className="nav-tab" activeClassName="nav-tab--active">
                                <span onClick={() => changeTab(1)}>{t('EditSurvey.Tab.Questions')}</span>
                            </TabGroup.Tab>
                            <TabGroup.Tab activeTab={activeTab} setActiveTab={changeTab} index={2} className="nav-tab" activeClassName="nav-tab--active">
                                <span onClick={() => changeTab(2)}>{t('EditSurvey.Tab.Outro')}</span>
                            </TabGroup.Tab>
                        </TabGroup.TabList>
                        <TabGroup.TabPanel
                            activeTab={activeTab}
                            setActiveTab={changeTab}
                            index={0}
                            className="bg-white rounded-b-lg transition-all transform"
                            activeClassName="opacity-100 duration-500 translate-x-0"
                            inactiveClassName="absolute left-0 right-0 top-12 opacity-0 -translate-x-2 h-0 overflow-hidden"
                        >
                            <div className="p-4 md:p-8">
                                <Heading variant="h2" classNames="mb-6">
                                    {t('EditSurvey.Heading.Intro')}
                                </Heading>
                                <TextArea
                                    label={t('EditSurvey.Label.Intro')}
                                    value={survey.intro}
                                    onChange={(e) => setSurvey({ ...survey, intro: e.target.value, version: survey.version + 1 })}
                                    classNames="mb-0"
                                    textAreaClassNames="field-sizing-content"
                                />
                            </div>
                        </TabGroup.TabPanel>
                        <TabGroup.TabPanel
                            activeTab={activeTab}
                            setActiveTab={changeTab}
                            index={1}
                            className="bg-white rounded-b-lg transition-all transform"
                            activeClassName="opacity-100 duration-500 translate-x-0"
                            inactiveClassName="absolute left-0 right-0 top-12 opacity-0 -translate-x-2 h-0 overflow-hidden"
                        >
                            <div className="p-4 md:p-8">
                                <Heading variant="h2" classNames="mb-6">
                                    {t('EditSurvey.Heading.Questions')}
                                </Heading>
                                {survey.questions.length === 0 && <div className="text-gray-500">{t('EditSurvey.NoQuestions')}</div>}
                                {survey.questions.map((question, index) => (
                                    <div className="w-full max-w-2xl flex mb-4" key={question.questionId}>
                                        <div className="flex flex-col -mr-px z-10">
                                            <div className="size-12 bg-white border-y border-l border-gray-200 rounded-l-lg rounded-br-lg inline-flex items-center justify-center font-medium">
                                                {`${index + 1}.`}
                                            </div>
                                            <Button disabled={index === 0} iconbtn onClick={() => moveQuestionUp(index)}>
                                                <SortArrow classNames="size-6 rotate-180 stroke-black" />
                                                <span className="sr-only">{t('EditQuestionnaire.Button.QuestionSortUp')}</span>
                                            </Button>
                                            <Button disabled={index === survey.questions.length - 1} iconbtn onClick={() => moveQuestionDown(index)}>
                                                <SortArrow classNames="size-6 stroke-black" />
                                                <span className="sr-only">{t('EditQuestionnaire.Button.QuestionSortDown')}</span>
                                            </Button>
                                        </div>
                                        <div className="flex-1 border border-gray-200 rounded-b-lg rounded-tr-lg p-4 menu:p-6">
                                            <div className="flex justify-end gap-4 xs:float-right ml-2 mb-2">
                                                <Button
                                                    data-testid={`survey-question-edit-button-${index}`}
                                                    variant="primary-outline"
                                                    iconbtn
                                                    onClick={() => setActiveQuestion(activeQuestion === question.questionId ? null : question.questionId)}
                                                >
                                                    <Edit classNames="size-6 stroke-current" />
                                                    <span className="sr-only">{t('EditQuestionnaire.Button.EditQuestion')}</span>
                                                </Button>
                                                <Button
                                                    data-testid={`survey-question-delete-button-${index}`}
                                                    variant="warning-outline"
                                                    iconbtn
                                                    onClick={() =>
                                                        setSurvey({
                                                            ...survey,
                                                            questions: survey.questions.filter((q) => q.questionId !== question.questionId),
                                                            version: survey.version + 1
                                                        })
                                                    }
                                                >
                                                    <Delete classNames="size-6 stroke-current" />
                                                    <span className="sr-only">{t('EditQuestionnaire.Button.DeleteQuestion')}</span>
                                                </Button>
                                            </div>
                                            {activeQuestion === question.questionId && (
                                                <EditQuestion
                                                    question={question}
                                                    updateQuestion={(updated) =>
                                                        setSurvey({
                                                            ...survey,
                                                            questions: survey.questions.map((q) => (q.questionId === question.questionId ? updated : q)),
                                                            version: survey.version + 1
                                                        })
                                                    }
                                                />
                                            )}
                                            {activeQuestion !== question.questionId && (
                                                <table className="inline">
                                                    <tbody>
                                                        <tr>
                                                            <td className="py-1 pr-2">{t('EditQuestionnaire.QuestionDetail.Question')}</td>
                                                            <td className="py-1 font-medium">{question.questionText}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="py-1 pr-2">{t('EditQuestionnaire.QuestionDetail.Options')}</td>
                                                            <td className="py-1 font-medium">
                                                                {!question.hasOtherOption
                                                                    ? question.options.length
                                                                    : `${question.options.length + 1}${t('EditQuestionnaire.QuestionDetail.OtherOptionText')}`}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td className="py-1 pr-2">{t('EditQuestionnaire.QuestionDetail.Required')}</td>
                                                            <td className="py-1 font-medium">{question.required ? 'Yes' : 'No'}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                <div className="flex flex-wrap gap-4 mt-10">
                                    <Button variant="primary" onClick={() => addQuestion(QuestionType.MultipleChoice)}>
                                        <Plus classNames="size-5 stroke-current -ms-1 me-1" />
                                        <span>{t('EditQuestionnaire.Button.AddMultipleChoiceQuestion')}</span>
                                    </Button>
                                    <Button
                                        data-testid="survey-add-single-choice-question-button"
                                        variant="primary"
                                        onClick={() => addQuestion(QuestionType.SingleChoice)}
                                    >
                                        <Plus classNames="size-5 stroke-current -ms-1 me-1" />
                                        <span>{t('EditQuestionnaire.Button.AddSingleChoiceQuestion')}</span>
                                    </Button>
                                    <Button variant="primary" onClick={() => addQuestion(QuestionType.Open)}>
                                        <Plus classNames="size-5 stroke-current -ms-1 me-1" />
                                        <span>{t('EditQuestionnaire.Button.AddOpenQuestion')}</span>
                                    </Button>
                                </div>
                            </div>
                        </TabGroup.TabPanel>
                        <TabGroup.TabPanel
                            activeTab={activeTab}
                            setActiveTab={changeTab}
                            index={2}
                            className="bg-white rounded-b-lg transition-all transform"
                            activeClassName="opacity-100 duration-500 translate-x-0"
                            inactiveClassName="absolute left-0 right-0 top-12 opacity-0 -translate-x-2 h-0 overflow-hidden"
                        >
                            <div className="p-4 md:p-8">
                                <Heading variant="h2" classNames="mb-6">
                                    {t('EditSurvey.Heading.Outro')}
                                </Heading>
                                <div className="max-w-lg">
                                    <TextArea
                                        label={t('EditSurvey.Label.Outro')}
                                        value={survey.outro}
                                        onChange={(e) => setSurvey({ ...survey, outro: e.target.value, version: survey.version + 1 })}
                                        classNames="mb-0"
                                        textAreaClassNames="field-sizing-content"
                                    />
                                </div>
                            </div>
                        </TabGroup.TabPanel>
                    </TabGroup>
                </div>
            </ContainerWebinarInner>
        </ContainerNoTopSpace>
    );
};
